import {Users, Wand2} from "lucide-react";
import * as m from "@/paraglide/messages";
import {Button} from "@/components/ui/button";
import {PersonaCard, PersonaCardSkeleton} from "./persona-card";
import type {AiPersonaResponse} from "@/lib/personas-api";
import {extractAiPersona} from "@/hooks/use-ai-personas";

const GRID_CLASS = "grid gap-4 lg:grid-cols-2 2xl:grid-cols-3";

interface PersonasGridProps {
  personas: AiPersonaResponse[];
  isLoading?: boolean;
  skeletonCount?: number;
  onPersonaClick?: (persona: AiPersonaResponse) => void;
  onPersonaChatClick?: (persona: AiPersonaResponse) => void;
  mentionCount?: number;
  onGenerate?: () => void;
}

export function PersonasGrid({
  personas,
  isLoading,
  skeletonCount = 4,
  onPersonaClick,
  onPersonaChatClick,
  mentionCount,
  onGenerate,
}: PersonasGridProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="bg-muted/30 h-5 w-48 animate-pulse rounded" />
        <div className={GRID_CLASS}>
          {Array.from({length: skeletonCount}, (_, i) => (
            <PersonaCardSkeleton
              key={i}
              style={{animationDelay: `${i * 100}ms`}}
              className="animate-in fade-in-0 duration-300"
            />
          ))}
        </div>
      </div>
    );
  }

  if (personas.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="from-muted/50 to-muted/30 ring-border/50 mb-5 flex h-20 w-20 items-center justify-center rounded-2xl bg-linear-to-br ring-1">
          <Users className="text-muted-foreground/60 h-9 w-9" />
        </div>
        <h3 className="mb-2 text-lg font-semibold">{m.personas_empty_title()}</h3>
        <p className="text-muted-foreground mb-6 max-w-sm text-center text-sm">
          {m.personas_empty_description()}
        </p>
        {onGenerate && (
          <Button onClick={onGenerate} size="lg">
            <Wand2 className="mr-2 h-4 w-4" />
            {m.personas_generate()}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {mentionCount !== undefined && mentionCount > 0 && (
        <div className="flex items-center gap-2">
          <div className="from-border/50 h-px flex-1 bg-linear-to-r to-transparent" />
          <span className="text-muted-foreground text-sm">
            {m.personas_mention_count({count: mentionCount})}
          </span>
          <div className="from-border/50 h-px flex-1 bg-linear-to-l to-transparent" />
        </div>
      )}

      <div className={GRID_CLASS}>
        {personas.map((personaResponse, index) => {
          const persona = extractAiPersona(personaResponse);
          return (
            <PersonaCard
              key={personaResponse.id}
              persona={persona}
              onClick={() => onPersonaClick?.(personaResponse)}
              onChatClick={() => onPersonaChatClick?.(personaResponse)}
              className="animate-in fade-in slide-in-from-bottom-2"
              style={{animationDelay: `${index * 50}ms`, animationFillMode: "backwards"}}
            />
          );
        })}
      </div>
    </div>
  );
}
